import React from 'react';
import { Sidebar } from './sidebar';
import { Header } from './header';
import { BottomNav } from './bottom-nav';
import { useFocusedFieldScroll } from '@/hooks/use-focused-field-scroll';

const SIDEBAR_COLLAPSE_KEY = 'atlas.sidebarCollapsed';

export function AppShell({ children }: { children: React.ReactNode }) {
  // One capturing focusin listener for the whole app: brings a newly focused
  // field — and its label — above the soft keyboard, replacing the browser's
  // instant jump with a loop that tracks the keyboard as it arrives. Mounted here
  // so it also covers fields inside dialogs, which portal outside this tree but
  // still resolve their own scroll container.
  useFocusedFieldScroll();

  // Desktop/landscape-tablet sidebar collapse (lg+). Below lg the sidebar is
  // hidden entirely and the bottom-nav handles navigation, so this only
  // affects the lg+ static sidebar. Persisted so the choice survives reloads.
  const [sidebarCollapsed, setSidebarCollapsed] = React.useState<boolean>(() => {
    try { return localStorage.getItem(SIDEBAR_COLLAPSE_KEY) === '1'; } catch { return false; }
  });
  React.useEffect(() => {
    try { localStorage.setItem(SIDEBAR_COLLAPSE_KEY, sidebarCollapsed ? '1' : '0'); } catch { /* ignore */ }
  }, [sidebarCollapsed]);

  return (
    <div className="flex min-h-screen">
      {/* `print:hidden` strips app-chrome from printed pages — sidebar,
          top header, mobile bottom-nav — so a report's print view shows
          just the content. Pair with `print:p-0` on `<main>` so the page
          uses the printer's margin instead of the on-screen padding. */}
      <div className="print:hidden contents"><Sidebar collapsed={sidebarCollapsed} /></div>
      {/* min-w-0 is mandatory on a flex child whose content can overflow horizontally —
         without it, flex-1 expands to fit content width and blows past the viewport,
         giving the whole app a horizontal scrollbar. Pages that need horizontal scroll
         must create it inside themselves (e.g. a Card with overflow-x-auto). */}
      <div className="flex-1 flex flex-col min-w-0">
        <div className="print:hidden contents">
          <Header onToggleSidebar={() => setSidebarCollapsed((c) => !c)} />
        </div>
        {/* pb-20 below lg reserves space for the fixed bottom-nav (h-16 + breathing
            room) — the bottom-nav now shows up to lg, so the cutover matches. */}
        {/* pb clears the fixed mobile bottom-nav (64px) + iOS home-indicator
            inset; env() is 0 on web so this stays the original 5rem there. */}
        <main className="flex-1 p-4 md:p-6 overflow-x-hidden pb-[calc(5rem_+_env(safe-area-inset-bottom))] lg:pb-6 print:p-0 print:pb-0">{children}</main>
      </div>
      <div className="print:hidden contents"><BottomNav /></div>
    </div>
  );
}
